// Les chiffres d'un document généré, confrontés à leurs sources (issue #160).
//
// L'IA reformule, et en reformulant elle arrondit, extrapole ou invente un
// « +40 % » qui sonne bien. `normalizeProfile` et `reconcileAdaptation`
// gardent les expériences et les liens, rien ne gardait les nombres. Repris de
// career-ops (`verify-cv-facts.mjs`) : tout nombre écrit en chiffres dans le
// document doit figurer dans une source, sinon on le SIGNALE. On ne bloque
// pas : l'utilisateur peut savoir que c'est vrai, il tranche.
//
// Seuls les nombres écrits en chiffres sont comparés : « trois ans » contre
// « 3 ans » ne se rapproche pas sans un dictionnaire par langue, et un faux
// signal coûte plus cher que le silence.

export interface ChiffreNonSourcé {
  /** Le nombre tel qu'écrit dans le document (« 40 % », « 1 200 »). */
  valeur: string
  /** Le passage qui le contient, pour le retrouver. */
  contexte: string
}

// Un nombre : chiffres, séparateurs de milliers (espace, point, virgule,
// apostrophe), décimale, et un éventuel signe de pourcentage collé ou non.
const NOMBRE = /\d[\d\s.,'’  ]*\d|\d/g

/** La valeur d'un nombre écrit, sans ses séparateurs : « 1 200 » et « 1,200 » donnent 1200, « 2,5 » et « 2.5 » donnent 2.5. */
export function valeurNombre(ecrit: string): string {
  const t = ecrit.replace(/[\s'’  ]/g, '')
  // Un seul séparateur suivi d'un ou deux chiffres : une décimale.
  const decimale = t.match(/^(\d+)[.,](\d{1,2})$/)
  if (decimale) return `${Number(decimale[1])}.${decimale[2].replace(/0+$/, '') || '0'}`.replace(/\.0$/, '')
  return String(Number(t.replace(/[.,]/g, '')))
}

/** Toutes les valeurs numériques présentes dans un texte. */
export function nombresDe(texte: string): Set<string> {
  const out = new Set<string>()
  for (const m of String(texte ?? '').matchAll(NOMBRE)) out.add(valeurNombre(m[0].trim().replace(/[.,]$/, '')))
  return out
}

// Champs qui ne portent pas d'affirmation : dates et coordonnées. Les dates
// viennent du maître par `reconcileAdaptation`, un téléphone n'est pas un fait.
const CHAMPS_IGNORES = new Set(['startDate', 'endDate', 'date', 'phone', 'email', 'url', 'addedAt', 'meta'])

/** Le texte d'un document (CV adapté ou lettre), champ par champ, sans les champs ignorés. */
function textesDe(valeur: unknown, cle = ''): string[] {
  if (CHAMPS_IGNORES.has(cle)) return []
  if (typeof valeur === 'string') return [valeur]
  if (typeof valeur === 'number') return [String(valeur)]
  if (Array.isArray(valeur)) return valeur.flatMap((v) => textesDe(v))
  if (valeur && typeof valeur === 'object') return Object.entries(valeur).flatMap(([k, v]) => textesDe(v, k))
  return []
}

const contexteAutour = (texte: string, debut: number, fin: number): string => {
  const a = Math.max(0, debut - 40)
  const b = Math.min(texte.length, fin + 40)
  return `${a > 0 ? '…' : ''}${texte.slice(a, b).trim()}${b < texte.length ? '…' : ''}`
}

/**
 * Les nombres du document absents de toutes les sources. `document` est un CV
 * (JSON Resume) ou une lettre (`paragraphs`…) ; `sources` est ce qui fait foi
 * (profil maître, faits d'enrichissement, et pour une lettre l'offre).
 * Un même nombre n'est signalé qu'une fois.
 */
export function chiffresNonSourcés(document: unknown, sources: unknown[]): ChiffreNonSourcé[] {
  const connus = new Set<string>()
  for (const s of sources) for (const t of textesDe(s)) for (const n of nombresDe(t)) connus.add(n)
  // Les dates du maître sont ignorées dans le document, pas dans les sources :
  // une année citée dans une phrase (« depuis 2019 ») se vérifie sur elles.
  for (const s of sources) {
    for (const t of JSON.stringify(s ?? '').match(/\d{4}/g) ?? []) connus.add(String(Number(t)))
  }
  const out: ChiffreNonSourcé[] = []
  const vus = new Set<string>()
  for (const texte of textesDe(document)) {
    for (const m of texte.matchAll(NOMBRE)) {
      const ecrit = m[0].trim().replace(/[.,]$/, '')
      const v = valeurNombre(ecrit)
      if (connus.has(v) || vus.has(v)) continue
      vus.add(v)
      const pourcent = /^\s*%/.test(texte.slice(m.index! + m[0].length))
      out.push({ valeur: pourcent ? `${ecrit} %` : ecrit, contexte: contexteAutour(texte, m.index!, m.index! + m[0].length) })
    }
  }
  return out
}
